import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type {
  ClaireTurn,
  ListClaireTurnsInput,
} from '@borradh-workspace/api-client/types';
import { ExternalLink, MessagesSquare } from 'lucide-react';
import { useClaireTurns } from '../../api/claire-turns';
import { OUTCOMES, formatDay } from './live-quality-format';
import { COMPLETION_LABELS } from './regression-format';

/** What the explorer was opened on: a title and the filter behind it. */
export interface ExplorerRequest {
  title: string;
  filter: ListClaireTurnsInput;
}

const MISTAKES = [
  'wrong claim',
  'request not actioned',
  'promise without handoff',
  'no reply',
  'times not offered',
  'duplicate link',
] as const;
const ANY = 'any';
/** The live judge's handoff verdicts, as the explorer names them. */
const HANDOFF_LABELS: Record<string, string> = {
  right: 'Handoff: staff needed',
  unneeded: 'Handoff: avoidable',
  missing: 'Should have handed off',
  none: 'No handoff needed',
};

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function OutcomeBadge({ outcome }: { outcome: ClaireTurn['outcome'] }) {
  const o = OUTCOMES.find((x) => x.filter === outcome);
  if (!o) return null;
  return <span className={cn('text-xs font-medium', o.text)}>{o.label}</span>;
}

function TurnCard({
  turn,
  beforeLabel,
}: {
  turn: ClaireTurn;
  /** What the before side is: the shipped reply, or main's replay. */
  beforeLabel: string;
}) {
  return (
    <li className="flex flex-col gap-2 rounded-lg border p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <OutcomeBadge outcome={turn.outcome} />
        {turn.completion ? (
          <span className="text-muted-foreground text-xs">
            ({COMPLETION_LABELS[turn.completion] ?? turn.completion})
          </span>
        ) : null}
        {turn.mistakes.map((m) => (
          <Badge key={m} variant="outline">
            {capitalise(m)}
          </Badge>
        ))}
        {turn.handoff && HANDOFF_LABELS[turn.handoff] ? (
          <Badge variant="secondary">{HANDOFF_LABELS[turn.handoff]}</Badge>
        ) : null}
        {turn.at ? (
          <span className="text-muted-foreground ml-auto text-xs">
            {new Date(turn.at).toLocaleString('en-GB', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ) : null}
      </div>
      <div data-customer-text className="flex flex-col gap-1">
        <p>
          <span className="text-muted-foreground">Customer: </span>
          {turn.customerMessage || '–'}
        </p>
        {turn.before ? (
          <p>
            <span className="text-muted-foreground">
              {beforeLabel}
              {turn.before.completion
                ? ` (${COMPLETION_LABELS[turn.before.completion] ?? turn.before.completion})`
                : ''}
              :{' '}
            </span>
            {turn.before.reply || '–'}
          </p>
        ) : null}
        <p>
          <span className="text-muted-foreground">
            {turn.before ? 'Replayed reply: ' : 'Claire: '}
          </span>
          {turn.reply || '(no reply)'}
        </p>
      </div>
      {turn.reason ? (
        <p className="text-muted-foreground italic">Judge: {turn.reason}</p>
      ) : null}
      <div className="flex gap-4 text-xs">
        {turn.conversationId ? (
          <a
            className="text-primary hover:underline"
            href={`/admin/harness?conversationId=${encodeURIComponent(turn.conversationId)}`}
            target="_blank"
            rel="noreferrer"
          >
            Open chat
          </a>
        ) : null}
        {turn.traceUrl ? (
          <a
            className="text-primary inline-flex items-center gap-1 hover:underline"
            href={turn.traceUrl}
            target="_blank"
            rel="noreferrer"
          >
            Trace in Langfuse <ExternalLink className="size-3" />
          </a>
        ) : null}
      </div>
    </li>
  );
}

function Filters({
  filter,
  onChange,
}: {
  filter: ListClaireTurnsInput;
  onChange: (f: ListClaireTurnsInput) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1">
        <Label htmlFor="explorer-outcome">Outcome</Label>
        <Select
          value={filter.outcome ?? ANY}
          onValueChange={(v) =>
            onChange({
              ...filter,
              outcome: v === ANY ? undefined : (v as typeof filter.outcome),
            })
          }
        >
          <SelectTrigger id="explorer-outcome" className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any outcome</SelectItem>
            {OUTCOMES.map((o) => (
              <SelectItem key={o.filter} value={o.filter}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="explorer-mistake">Mistake</Label>
        <Select
          value={filter.mistake ?? ANY}
          onValueChange={(v) =>
            onChange({
              ...filter,
              mistake: v === ANY ? undefined : (v as typeof filter.mistake),
            })
          }
        >
          <SelectTrigger id="explorer-mistake" className="w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any or none</SelectItem>
            <SelectItem value="serious">Any serious mistake</SelectItem>
            <SelectItem value="none">No mistake</SelectItem>
            {MISTAKES.map((m) => (
              <SelectItem key={m} value={m}>
                {capitalise(m)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {filter.source === 'live' ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor="explorer-handoff">Live judge</Label>
          <Select
            value={filter.handoff ?? ANY}
            onValueChange={(v) =>
              onChange({
                ...filter,
                handoff: v === ANY ? undefined : (v as typeof filter.handoff),
              })
            }
          >
            <SelectTrigger id="explorer-handoff" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any verdict</SelectItem>
              <SelectItem value="right">{HANDOFF_LABELS.right}</SelectItem>
              <SelectItem value="unneeded">
                {HANDOFF_LABELS.unneeded}
              </SelectItem>
              <SelectItem value="missing">{HANDOFF_LABELS.missing}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : null}
      {filter.source === 'run' ? (
        <>
          <div className="flex flex-col gap-1">
            <Label htmlFor="explorer-side">Outcome of</Label>
            <Select
              value={filter.side ?? 'after'}
              onValueChange={(v) =>
                onChange({ ...filter, side: v as 'before' | 'after' })
              }
            >
              <SelectTrigger id="explorer-side" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="before">
                  {filter.judge === 'v2'
                    ? "Before (main's replay)"
                    : 'Before (shipped)'}
                </SelectItem>
                <SelectItem value="after">After (replay)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch
              id="explorer-worse"
              checked={filter.worse === true}
              onCheckedChange={(worse) => onChange({ ...filter, worse })}
            />
            <Label htmlFor="explorer-worse">Got worse in this run</Label>
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * The real conversations behind a number: opened from any count on the
 * page, filterable, newest (or, for "got worse", worst) first.
 */
export function TurnExplorer({
  request,
  onChange,
  onClose,
}: {
  request: ExplorerRequest | null;
  onChange: (r: ExplorerRequest) => void;
  onClose: () => void;
}) {
  const { data, isLoading, isError, error } = useClaireTurns(
    request?.filter ?? null
  );
  const filter = request?.filter;
  const range =
    filter?.source === 'live'
      ? filter.from === filter.to
        ? formatDay(filter.from)
        : `${formatDay(filter.from)} – ${formatDay(filter.to)}`
      : null;
  return (
    <Sheet open={request !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{request?.title ?? 'Conversations'}</SheetTitle>
          <SheetDescription>
            {range
              ? `Production customer messages, ${range} (UK days).`
              : 'Conversations replayed in this test run.'}{' '}
            Phone numbers and emails are hidden.
          </SheetDescription>
        </SheetHeader>
        {request && filter ? (
          <div className="flex flex-col gap-4 px-4 pb-6">
            <Filters
              filter={filter}
              onChange={(f) => onChange({ ...request, filter: f })}
            />
            {isLoading ? (
              <div className="flex flex-col gap-3">
                <Skeleton className="h-28 w-full" />
                <Skeleton className="h-28 w-full" />
              </div>
            ) : isError || !data ? (
              <Alert variant="destructive">
                <AlertTitle>Could not load the conversations</AlertTitle>
                <AlertDescription>
                  {error?.message ?? 'The request failed.'}
                </AlertDescription>
              </Alert>
            ) : !data.configured ? (
              <Alert>
                <AlertTitle>Langfuse is not connected on this API</AlertTitle>
              </Alert>
            ) : data.turns.length === 0 ? (
              <div className="text-muted-foreground flex flex-col items-center gap-2 py-10 text-sm">
                <MessagesSquare className="size-6" />
                No conversations match.
              </div>
            ) : (
              <>
                <p className="text-muted-foreground text-sm">
                  {data.total > data.turns.length
                    ? `Showing ${data.turns.length} of ${data.total}${data.capped ? '+' : ''}.`
                    : `${data.total} ${data.total === 1 ? 'conversation' : 'conversations'}.`}
                </p>
                <ul className="flex flex-col gap-3">
                  {data.turns.map((t) => (
                    <TurnCard
                      key={t.id}
                      turn={t}
                      beforeLabel={
                        filter.source === 'run' && filter.judge === 'v2'
                          ? "Main's reply"
                          : 'Shipped reply'
                      }
                    />
                  ))}
                </ul>
              </>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
